import { useEffect, useState } from 'react';
import { employeeInvitationSettingsGateway } from '../EmployeeInvitationSettingsGateway';
import { lifetimeText } from '../lifetimeText';
import type { EmployeeInvitationSettings } from '../types';
import { friendlyErrorMessage } from '../../../shared/friendlyError';

/** Preset lifetimes offered besides "platform default" and "custom". */
const PRESET_HOURS = [24, 48, 168] as const;

type Choice = 'default' | 'custom' | (typeof PRESET_HOURS)[number];

function choiceFor(settings: EmployeeInvitationSettings): Choice {
  const own = settings.companyLifetimeHours;
  if (own === null) return 'default';
  return (PRESET_HOURS as readonly number[]).includes(own) ? (own as Choice) : 'custom';
}

/** Employee Work Setup (EPN-205): where a company sets how long an employee invitation link stays valid. A first section of this page; it applies
 * to invitations sent after it is saved, and an invitation already sent keeps its own expiry. */
export function EmployeeWorkSetupPage() {
  const [settings, setSettings] = useState<EmployeeInvitationSettings | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [choice, setChoice] = useState<Choice>('default');
  const [customHours, setCustomHours] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const adopt = (next: EmployeeInvitationSettings) => {
    setSettings(next);
    setChoice(choiceFor(next));
    setCustomHours(next.companyLifetimeHours === null ? '' : String(next.companyLifetimeHours));
  };

  useEffect(() => {
    const controller = new AbortController();
    employeeInvitationSettingsGateway
      .get(controller.signal)
      .then(adopt)
      .catch((err) => {
        if (!controller.signal.aborted) setLoadError(friendlyErrorMessage(err, 'Could not load the invitation settings.'));
      });
    return () => controller.abort();
  }, []);

  const parsedCustom = /^\d+$/.test(customHours.trim()) ? Number(customHours.trim()) : null;
  const customError =
    choice === 'custom' && settings && (parsedCustom === null || parsedCustom < 1 || parsedCustom > settings.maxLifetimeHours)
      ? `Enter a whole number of hours from 1 to ${settings.maxLifetimeHours}.`
      : null;

  const selectedHours = (): number | null => (choice === 'default' ? null : choice === 'custom' ? parsedCustom : choice);

  const save = async () => {
    setSaveError(null);
    setSaved(false);
    setSaving(true);
    try {
      adopt(await employeeInvitationSettingsGateway.save(selectedHours()));
      setSaved(true);
    } catch (err) {
      setSaveError(friendlyErrorMessage(err, 'Could not save the invitation settings.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="card card--wide">
      <div className="section-header">
        <h2>Employee Work Setup</h2>
      </div>

      {loadError && <p className="error" role="alert">{loadError}</p>}
      {!settings && !loadError && <p role="status">Loading…</p>}

      {settings && (
        <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
          <legend style={{ padding: 0 }}><h3 style={{ margin: 0 }}>Employee invitations</h3></legend>
          <p className="muted">
            How long the link in an employee invitation email stays valid. It applies to invitations sent after you save; an
            invitation that has already been sent keeps its original expiry. Resending an invitation starts a new lifetime.
          </p>

          <label style={{ display: 'block', margin: '8px 0' }}>
            <input type="radio" name="invitation-lifetime" checked={choice === 'default'} onChange={() => setChoice('default')} />{' '}
            Use the platform default ({lifetimeText(settings.defaultLifetimeHours)})
          </label>
          {PRESET_HOURS.map((hours) => (
            <label key={hours} style={{ display: 'block', margin: '8px 0' }}>
              <input type="radio" name="invitation-lifetime" checked={choice === hours} onChange={() => setChoice(hours)} />{' '}
              {lifetimeText(hours)}
            </label>
          ))}
          <label style={{ display: 'block', margin: '8px 0' }}>
            <input type="radio" name="invitation-lifetime" checked={choice === 'custom'} onChange={() => setChoice('custom')} /> Custom
          </label>
          {choice === 'custom' && (
            <label style={{ display: 'block', margin: '8px 0 8px 24px' }}>
              Hours (1 to {settings.maxLifetimeHours})
              <input
                type="text"
                inputMode="numeric"
                value={customHours}
                onChange={(e) => setCustomHours(e.target.value)}
                aria-invalid={customError ? true : undefined}
                aria-describedby={customError ? 'invitation-lifetime-error' : undefined}
                style={{ marginLeft: 8, width: 100 }}
              />
              {customError && <span id="invitation-lifetime-error" className="error" role="alert" style={{ marginLeft: 8 }}>{customError}</span>}
            </label>
          )}

          <p className="muted" role="status">
            New invitations currently expire after {lifetimeText(settings.lifetimeHours)}.
          </p>
          {saveError && <p className="error" role="alert">{saveError}</p>}
          {saved && <p className="muted" role="status">Invitation settings saved.</p>}
          <div style={{ marginTop: 12 }}>
            <button type="button" onClick={() => void save()} disabled={saving || customError !== null}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </fieldset>
      )}
    </section>
  );
}
